// Token-level prefix trie for passage-constrained CTC decoding.
//
// Each path through the trie represents one passage word's BPE token sequence.
// Words are inserted by tokenizing them with a greedy maximal-munch BPE pass
// over the model vocab — no separate merge-rules file required.
//
// Trie state is advanced token-by-token during beam search. A node being
// `isWordEnd` signals that the tokens emitted since the last word boundary
// form a valid passage word, triggering the word-completion bonus.

// SentencePiece word-boundary marker (U+2581 LOWER ONE EIGHTH BLOCK).
// NeMo BPE vocab uses this to mark tokens that start a new word.
const SP_WORD_BOUNDARY = "▁";

export class TokenPrefixTrie {
  constructor() {
    // Each node: { children: Map<tokenId, node>, isWordEnd: bool }
    this.root = { children: new Map(), isWordEnd: false };
  }

  // Insert a pre-tokenized word (array of token IDs) into the trie.
  // wordId — stable integer id for n-gram LM scoring at word boundaries.
  insert(tokenIds, wordId = undefined) {
    let node = this.root;
    for (const id of tokenIds) {
      let child = node.children.get(id);
      if (!child) {
        child = { children: new Map(), isWordEnd: false, wordId: undefined };
        node.children.set(id, child);
      }
      node = child;
    }
    node.isWordEnd = true;
    if (wordId !== undefined) node.wordId = wordId;
  }

  // Advance trie state by one token. Returns the child node or null (off-trie).
  step(node, tokenId) {
    return node.children.get(tokenId) ?? null;
  }

  // Build a trie from an array of word strings using the model vocab for BPE.
  // Words that cannot be encoded (e.g. contain characters outside the vocab
  // subword alphabet) are silently dropped — they will never match anyway.
  static fromWordList(wordList, vocab, wordToId = null) {
    const trie = new TokenPrefixTrie();
    const vocabMap = buildVocabMap(vocab);
    const ids = wordToId ?? buildWordToId(wordList);
    for (const word of wordList) {
      const wl = word.toLowerCase();
      const tokenIds = greedyTokenize(word, vocabMap);
      if (tokenIds !== null) trie.insert(tokenIds, ids.get(wl));
    }
    return trie;
  }
}

// Build a reverse lookup map: token string → token ID.
// Called once per trie construction, not in the hot loop.
function buildVocabMap(vocab) {
  const m = new Map();
  for (let i = 0; i < vocab.length; i++) m.set(vocab[i], i);
  return m;
}

function buildWordToId(wordList) {
  const m = new Map();
  for (const word of wordList) {
    const wl = word.toLowerCase();
    if (!m.has(wl)) m.set(wl, m.size);
  }
  return m;
}

// Greedy longest-match BPE tokenizer.
//
// SentencePiece BPE subwords are chosen so that a greedy maximal-munch scan
// over the vocab reproduces the model's tokenization for most common words.
// Prepend SP_WORD_BOUNDARY to mark the token as word-initial, matching the
// convention the model was trained with.
//
// Returns an array of token IDs, or null if the word is out-of-vocabulary.
function greedyTokenize(word, vocabMap) {
  const s = SP_WORD_BOUNDARY + word.toLowerCase();
  const ids = [];
  let pos = 0;
  while (pos < s.length) {
    let bestLen = 0;
    let bestId = -1;
    // Try longest prefix first; a BPE vocab always contains single characters
    // so if nothing longer matches, the single character will.
    for (let len = s.length - pos; len > 0; len--) {
      const id = vocabMap.get(s.slice(pos, pos + len));
      if (id !== undefined) {
        bestLen = len;
        bestId = id;
        break;
      }
    }
    if (bestLen === 0) return null;
    ids.push(bestId);
    pos += bestLen;
  }
  return ids;
}
